import React from 'react';

/**
 * Props for the AutostereogramControls component
 */
interface AutostereogramControlsProps {
  /**
   * Current depth value
   */
  depth: number;
  /**
   * Handler for depth changes
   */
  onDepthChange: (depth: number) => void;
  /**
   * Current square size value (as a fraction)
   */
  squareSize: number;
  /**
   * Handler for square size changes
   */
  onSquareSizeChange: (size: number) => void;
  /**
   * Current separation value
   */
  separation: number;
  /**
   * Handler for separation changes
   */
  onSeparationChange: (separation: number) => void;
  /**
   * Current random seed value
   */
  seed: number;
  /**
   * Handler for seed changes
   */
  onSeedChange: (seed: number) => void;
  /**
   * Handler for regenerating with a random seed
   */
  onRandomSeed: () => void;
}

/**
 * Control panel for adjusting autostereogram parameters
 */
export const AutostereogramControls: React.FC<AutostereogramControlsProps> = ({
  depth,
  onDepthChange,
  squareSize,
  onSquareSizeChange,
  separation,
  onSeparationChange,
  seed,
  onSeedChange,
  onRandomSeed,
}) => {
  /**
   * Handle slider changes and convert to appropriate values
   */
  const handleDepthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onDepthChange(parseInt(e.target.value, 10));
  };

  const handleSquareSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSquareSizeChange(parseFloat(e.target.value));
  };

  const handleSeparationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSeparationChange(parseInt(e.target.value, 10));
  };

  const handleSeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSeedChange(parseInt(e.target.value, 10));
  };

  return (
    <div
      style={{
        maxWidth: '500px',
        margin: '20px auto',
        padding: '15px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        backgroundColor: '#f9f9f9',
      }}
    >
      <h3 style={{ marginTop: '0', textAlign: 'center' }}>Autostereogram Controls</h3>

      <div style={{ marginBottom: '15px' }}>
        <label htmlFor='depth' style={{ display: 'block', marginBottom: '5px' }}>
          Depth: {depth}
        </label>
        <input
          type='range'
          id='depth'
          min='-50'
          max='50'
          value={depth}
          onChange={handleDepthChange}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8em' }}>
          <span>Shallow</span>
          <span>Deep</span>
        </div>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label htmlFor='squareSize' style={{ display: 'block', marginBottom: '5px' }}>
          Square Size: {squareSize.toFixed(2)}
        </label>
        <input
          type='range'
          id='squareSize'
          min='0.1'
          max='0.4'
          step='0.01'
          value={squareSize}
          onChange={handleSquareSizeChange}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8em' }}>
          <span>Small</span>
          <span>Large</span>
        </div>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label htmlFor='separation' style={{ display: 'block', marginBottom: '5px' }}>
          Pattern Separation: {separation}px
        </label>
        <input
          type='range'
          id='separation'
          min='10'
          max='100'
          value={separation}
          onChange={handleSeparationChange}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8em' }}>
          <span>Close</span>
          <span>Far</span>
        </div>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label htmlFor='seed' style={{ display: 'block', marginBottom: '5px' }}>
          Pattern Seed: {seed}
        </label>
        <input
          type='number'
          id='seed'
          value={seed}
          onChange={handleSeedChange}
          style={{ width: '80px', marginRight: '10px' }}
        />
        <button
          onClick={onRandomSeed}
          style={{
            padding: '5px 10px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Random Seed
        </button>
      </div>

      <div style={{ marginTop: '20px', fontSize: '0.9em' }}>
        <p>
          <strong>Viewing Tips:</strong>
        </p>
        <ul>
          <li>Try to focus your eyes behind the image (as if looking at a distant object)</li>
          <li>Alternatively, try to cross your eyes slightly</li>
          <li>Wait for the hidden 3D square to emerge</li>
          <li>Try larger separation values if you're having difficulty seeing the 3D effect</li>
          <li>Moderate depth values (10-25) often work best</li>
        </ul>
      </div>
    </div>
  );
};

export default AutostereogramControls;
